import Box from "@mui/joy/Box";
import Sheet from "@mui/joy/Sheet";
import { CircularProgress, Stack } from "@mui/joy";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { homeThoughtRecordListAtom } from "recoil/homeThoughtRecordListAtom";
import Overview from "./Overview";
import LearningActivityList from "./LearningActivityList";
import ThoughtRecordList from "./ThoughtRecordList";
import MeditationList from "./MeditationList";

import {
  HOME_DOMAIN,
  HOME_MEDITATION_LIST_ENDPOINT,
  HOME_TASK_LIST_ENDPOINT,
  HOME_THOUGHTRECORD_LIST_ENDPOINT,
  homeApi,
} from "api/homeApi";
import React, { useCallback, useEffect, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import useIsInappWebview from "hooks/useIsInappWebview";
import { hasAuthHeader } from "api";

export default function HomeTaskListForPatient() {
  const userState = useRecoilValue(userAtom);
  const isInappWebview = useIsInappWebview();
  const setHomeThoughtRecordList = useSetRecoilState(homeThoughtRecordListAtom);

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const thoughtRecordListQueryFn = useCallback(
    () => homeApi.getThoughtRecordList(userState?.userId!!),
    [userState]
  );

  const meditationListQueryFn = useCallback(
    () => homeApi.getMeditationList(userState?.userId!!),
    [userState]
  );

  const taskListQueryFn = useCallback(
    () => homeApi.getList(userState?.userId!!),
    [userState]
  );

  const results = useQueries({
    queries: [
      {
        enabled: hasAuthHeader() && !!userState?.userId,
        refetchInterval: 10000,
        // done/undone 낙관적 캐시 반영 직후 마운트/포커스 refetch 가 stale 값으로 덮어쓰지
        // 않도록 짧은 staleTime 으로 그 윈도우를 건너뛴다. refetchInterval/invalidate 는 영향 없음.
        staleTime: 2000,
        queryKey: [
          HOME_DOMAIN,
          HOME_THOUGHTRECORD_LIST_ENDPOINT,
          userState?.userId,
        ],
        queryFn: thoughtRecordListQueryFn,
      },
      {
        enabled: hasAuthHeader() && !!userState?.userId,
        refetchInterval: 10000,
        staleTime: 2000,
        queryKey: [
          HOME_DOMAIN,
          HOME_MEDITATION_LIST_ENDPOINT,
          userState?.userId,
        ],
        queryFn: meditationListQueryFn,
      },
      {
        enabled: hasAuthHeader() && !!userState?.userId,
        refetchInterval: 10000,
        // 활동지 done 낙관적 캐시 반영 직후 마운트/포커스 refetch 가 stale 값으로 덮어쓰지
        // 않도록 짧은 staleTime 으로 그 윈도우를 건너뛴다. refetchInterval/invalidate 는 영향 없음.
        staleTime: 2000,
        queryKey: [HOME_DOMAIN, HOME_TASK_LIST_ENDPOINT, userState?.userId],
        queryFn: taskListQueryFn,
      },
    ],
  });
  useEffect(() => {
    if (results) {
      if (
        !userState?.userId
          ? !results[2].isLoading
          : results.every((element) => !element.isLoading)
      ) {
        setIsInitialLoading(false);
      } else {
        setIsInitialLoading(true);
      }
    }
  }, [results, userState?.userId]);

  useEffect(() => {
    if (results[0].data?.result) {
      setHomeThoughtRecordList(results[0].data.result);
    }
  }, [results, setHomeThoughtRecordList]);

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        ...(isInappWebview && { px: "20px" }),
        ...(isInappWebview
          ? {
              mt: 1,
            }
          : {
              mt: "20px",
            }),
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          flexGrow: 1,
          display: "flex",
        }}
      >
        <Stack
          sx={{
            width: "100%",
          }}
        >
          <Overview />

          {isInitialLoading ? (
            <Stack
              justifyContent={"center"}
              alignItems="center"
              sx={{
                flexGrow: 1,
              }}
            >
              <CircularProgress
                sx={{
                  mt: "14px",
                  mx: "auto",
                }}
                color="neutral"
              />
            </Stack>
          ) : (
            <Stack mt={isInappWebview ? 0 : "16px"}>
              {results[0].data?.result && (
                <ThoughtRecordList data={results[0].data?.result} />
              )}
              {results[1].data?.result && (
                <MeditationList data={results[1].data?.result} />
              )}
              {results[2].data?.result && (
                <LearningActivityList
                  data={results[2].data?.result}
                  dataUpdatedAt={results[2].dataUpdatedAt}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </Sheet>
  );
}
